import React, { useEffect, useState } from 'react';
import { createTableColumns } from './tableColumns';
import { useDiscoveryInventoryTable } from './hooks/useDiscoveryInventoryTable';
import { DraggableColumnHeader } from './components/DraggableColumnHeader';
import { BaseDiscoveryTable } from '../../../shared';
import { SendToMigrationModal, DragSelectedItem, WarningModal, Typography } from '../../../../../components';
import { useDraggable, DragOverlay } from '@dnd-kit/core';
import { snapCenterToCursor } from '@dnd-kit/modifiers';

const { Text } = Typography;

/**
 * DiscoveryInventoryTable - Extends BaseDiscoveryTable with inventory-specific features
 * Adds: SendToMigrationModal, inventory-specific columns
 */
export const DiscoveryInventoryTable = ({
    selectedColumns,
    tableData,
    isLoading,
    isFetching,
    pagination,
    handlePaginationChange,
    onRowClick,
    selectedRowData,
    onSelectionChange,
    handleChangeReadyStatus,
    handleSendToMigration,
    onSortChange,
    sortField,
    sortOrder,
    showSendToMigrationModal,
    setShowSendToMigrationModal,
    moveGroups = [],
    hasVMsWithoutMoveGroup = false,
    enableRowDragging = false,
}) => {
    const [draggedRow, setDraggedRow] = useState(null);

    const columns = createTableColumns({
        handleChangeReadyStatus,
    });
    const {
        data,
        rowSelection,
        visibleColumns,
        selectedRowKeys,
        selectedRows,
        columnOrder,
        handleDragEnd,
        setSelectedRowKeys,
    } = useDiscoveryInventoryTable({ tableData, columns, selectedColumns, sortField, sortOrder });

    useEffect(() => {
        onSelectionChange?.(selectedRowKeys, selectedRows);
    }, [selectedRowKeys, selectedRows, onSelectionChange]);

    const [showFailedVMsModal, setShowFailedVMsModal] = useState(false);
    const [failedVMs, setFailedVMs] = useState([]);

    const handleSortingUpdate = (pagination, filters, sorter) => {
        onSortChange?.(sorter);
    };

    // Custom row wrapper to make rows draggable (for move groups & apps page)
    const DraggableRow = ({ record, children, ...props }) => {
        if (!record || !enableRowDragging) {
            return <tr {...props}>{children}</tr>;
        }

        const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
            id: `inventory-row-${record.id}`,
            data: {
                type: 'vm',
                sourceId: record.id,
                sourceIds:
                    selectedRowKeys.includes(record.id) && selectedRowKeys.length > 1 ? selectedRowKeys : [record.id],
                record,
            },
        });

        // Track dragged row for overlay
        useEffect(() => {
            if (isDragging) {
                setDraggedRow(record);
            } else if (draggedRow?.id === record.id) {
                setDraggedRow(null);
            }
        }, [isDragging]);

        return (
            <tr
                ref={setNodeRef}
                {...props}
                {...attributes}
                {...listeners}
                style={{
                    ...props.style,
                    cursor: isDragging ? 'grabbing' : 'grab',
                }}
            >
                {children}
            </tr>
        );
    };

    return (
        <div>
            <BaseDiscoveryTable
                tableData={data}
                isLoading={isLoading}
                isFetching={isFetching}
                pagination={pagination}
                handlePaginationChange={handlePaginationChange}
                onRowClick={onRowClick}
                selectedRowData={selectedRowData}
                rowSelection={rowSelection}
                visibleColumns={visibleColumns}
                columnOrder={columnOrder}
                handleDragEnd={handleDragEnd}
                handleSortingUpdate={handleSortingUpdate}
                DraggableColumnHeader={DraggableColumnHeader}
                className="discovery-inventory-table"
                enableRowDragging={enableRowDragging}
                customComponents={
                    enableRowDragging
                        ? {
                              body: {
                                  row: (props) => {
                                      const rowKey = props['data-row-key'];
                                      const record = rowKey ? data.find((r) => r.id === rowKey) : null;
                                      return <DraggableRow {...props} record={record} />;
                                  },
                              },
                          }
                        : undefined
                }
            />
            <SendToMigrationModal
                open={showSendToMigrationModal}
                onCancel={() => setShowSendToMigrationModal(false)}
                onContinue={async () => {
                    setShowSendToMigrationModal(false);
                    const result = await handleSendToMigration(selectedRowKeys);
                    setSelectedRowKeys([]);
                    if (result?.failedVMs?.length > 0) {
                        setFailedVMs(result.failedVMs);
                        setShowFailedVMsModal(true);
                    }
                }}
                showMoveGroupAssignment={hasVMsWithoutMoveGroup}
                moveGroups={moveGroups}
            />
            {enableRowDragging && (
                <DragOverlay dropAnimation={null} modifiers={[snapCenterToCursor]}>
                    {draggedRow ? (
                        <DragSelectedItem
                            label={draggedRow.name || 'Item'}
                            count={
                                selectedRowKeys.includes(draggedRow.id) && selectedRowKeys.length > 1
                                    ? selectedRowKeys.length
                                    : 1
                            }
                        />
                    ) : null}
                </DragOverlay>
            )}
            <WarningModal open={showFailedVMsModal} onOk={() => setShowFailedVMsModal(false)}>
                <Text>There was an error sending the following VM(s) to migration:</Text>
                <ul>
                    {failedVMs.map((vm) => (
                        <li key={vm.id}>
                            <Text>
                                {vm.name} - {vm.ipAddress}
                            </Text>
                        </li>
                    ))}
                </ul>
                <Text>If the problem persists, please contact RiverMeadow Support</Text>
            </WarningModal>
        </div>
    );
};
